import type { HTMLAttributes, ReactNode } from "react";
import { cn } from "../utils";

export type BandedSectionVariant = "plain" | "sky" | "warm" | "gradient";

export interface BandedSectionProps extends HTMLAttributes<HTMLElement> {
  variant?: BandedSectionVariant;
  /** Optional extra classes for the content container */
  contentClassName?: string;
  children: ReactNode;
}

const variantClasses: Record<BandedSectionVariant, string> = {
  plain: "bg-transparent",
  sky: "bg-transparent",
  warm: "bg-transparent",
  gradient: "bg-gradient-to-br from-primary/10 via-secondary/10 to-accent/10",
};

/**
 * Full-width banded section wrapper with centered content container.
 */
export function BandedSection({
  variant = "plain",
  className,
  contentClassName,
  children,
  style,
  ...props
}: BandedSectionProps) {
  const backgroundStyle: React.CSSProperties = {
    ...(variant === "sky"
      ? { backgroundColor: "rgba(78, 172, 226, 0.12)" }
      : variant === "warm"
        ? { backgroundColor: "rgba(233, 30, 140, 0.08)" }
        : null),
  };
  const overlay =
    variant === "sky"
      ? "bg-gradient-to-b from-white/30 via-white/10 to-transparent"
      : variant === "warm"
        ? "bg-gradient-to-b from-white/25 via-white/10 to-transparent"
        : null;

  return (
    <section
      className={cn(
        "relative w-full py-12 md:py-16",
        variantClasses[variant],
        className
      )}
      style={{ ...backgroundStyle, ...style }}
      {...props}
    >
      {overlay && <div className={cn("pointer-events-none absolute inset-0", overlay)} aria-hidden="true" />}
      <div className={cn("relative mx-auto max-w-6xl px-6", contentClassName)}>
        {children}
      </div>
    </section>
  );
}
